import { type ComputedRef, computed, type MaybeRefOrGetter, toValue } from "vue";
import { showExcursionOccurrenceDates } from "@/composables/authorization.js";
import {
  formatOccurrenceDateRange,
  hasFinalizedOccurrenceDates,
  type OccurrenceDateFields,
} from "../utils/occurrenceDates";

interface UseExcursionOccurrenceDatesReturn {
  formattedDateRange: ComputedRef<string>;
  showOccurrenceDates: ComputedRef<boolean>;
  showSwitchDatesMessage: ComputedRef<boolean>;
}

type OccurrenceWithEventId = OccurrenceDateFields & { event_id?: string };

/**
 * Occurrence date display for excursion cards, gated by the show-excursion-occurrence-dates flag.
 * `sessionExcursions` is a set of occurrences shown in the configurator (included or optional),
 * used to detect sibling occurrences of the same event for the "switch dates later" message.
 */
export const useExcursionOccurrenceDates = <T extends OccurrenceWithEventId>(
  excursion: MaybeRefOrGetter<T | undefined>,
  sessionExcursions: MaybeRefOrGetter<T[] | undefined>,
): UseExcursionOccurrenceDatesReturn => {
  const hasFinalizedDates = computed<boolean>(() => hasFinalizedOccurrenceDates(toValue(excursion)));

  const showOccurrenceDates = computed<boolean>(
    () => Boolean(showExcursionOccurrenceDates.value) && hasFinalizedDates.value,
  );

  const formattedDateRange = computed<string>(() => {
    const occurrence = toValue(excursion);
    if (!showOccurrenceDates.value || !occurrence?.start_date_time || !occurrence?.end_date_time) {
      return "";
    }
    return formatOccurrenceDateRange(occurrence.start_date_time, occurrence.end_date_time);
  });

  const hasMultipleOccurrencesForEvent = computed<boolean>(() => {
    const eventId = toValue(excursion)?.event_id;
    if (!eventId) {
      return false;
    }
    const occurrencesForEvent = toValue(sessionExcursions)?.filter((e) => e.event_id === eventId) ?? [];
    return occurrencesForEvent.length > 1;
  });

  const showSwitchDatesMessage = computed<boolean>(
    () => showOccurrenceDates.value && hasMultipleOccurrencesForEvent.value,
  );

  return {
    formattedDateRange,
    showOccurrenceDates,
    showSwitchDatesMessage,
  };
};
